import React from 'react';
import {
  Container,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CTAButton from './CTAButton';

const HeroSection = ({ onPricingClick, onDemoClick }) => {
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
    if (onPricingClick) {
      onPricingClick();
    }
  };

  const handleDemoClick = () => {
    window.location.href = '/demo';
    if (onDemoClick) {
      onDemoClick();
    }
  };

  return (
    <Box
      sx={{
        minHeight: { xs: 'calc(100vh - 56px)', md: 'calc(100vh - 64px)' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        textAlign: 'center',
        py: { xs: 1, md: 0 },
        background: 'linear-gradient(180deg, #f8f9fe 0%, rgba(255, 255, 255, 0.95) 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-50%',
          width: '200%',
          height: '100%',
          background: 'radial-gradient(circle at center, rgba(25, 118, 210, 0.03) 0%, transparent 70%)',
          zIndex: 0
        }
      }}
    >
      {/* Top Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Main Hero Content - Centered */}
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Left Column: Text Content */}
            <Box sx={{ 
              flexGrow: 1, 
              flexBasis: { xs: '100%', md: '60%' }, 
              maxWidth: { xs: '100%', md: '60%' }, 
              minWidth: 0, 
              textAlign: { xs: 'center', md: 'left' } 
            }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: { xs: 2, md: 2 },
                  fontSize: { xs: '2.5rem', sm: '2.75rem', md: '3.125rem' },
                  lineHeight: { xs: 1.2, md: 1.2 },
                  background: 'linear-gradient(135deg, #0A2540 0%, #1976d2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                  px: { xs: 1, md: 0 }
                }}
              >
                {/* Turn your Reviews into Customer Experience Insights */}
                Cost-Effective Customer Experience Insights
              </Typography>

              {/* Review Platforms - Mobile only, below title */}
              <Box sx={{ 
                display: { xs: 'block', md: 'none' },
                textAlign: 'center',
                mb: 2,
                px: { xs: 1, md: 0 }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 2.5
                }}>
                  <img 
                    src="/images/sources/google-logo.png" 
                    alt="Google Reviews" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/tripadvisor-logo.png" 
                    alt="TripAdvisor" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/trustpilot-logo.png" 
                    alt="Trustpilot" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />

                  <img 
                    src="/images/sources/yelp-logo.png" 
                    alt="Yelp" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/booking-logo.png" 
                    alt="Booking.com" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/justeat-logo.png" 
                    alt="Just Eat" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/deliveroo-logo.png" 
                    alt="Deliveroo" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/ubereats-logo.png" 
                    alt="Uber Eats" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/designmynight-logo.png" 
                    alt="Design My Night" 
                    style={{ height: '32px', width: 'auto', opacity: 0.8 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ 
                  color: '#78909C', 
                  fontSize: '0.75rem',
                  fontStyle: 'italic',
                  mt: 0.5
                }}>
                  + Custom integrations
                </Typography>
              </Box>
              
              <Typography
                variant="h5"
                sx={{
                  mb: { xs: 3, md: 3 },
                  color: '#546E7A',
                  fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.25rem' },
                  lineHeight: { xs: 1.4, md: 1.5 },
                  fontWeight: 400,
                  px: { xs: 1, md: 0 }
                }}
              >
                Get actionable insights in minutes. Start analyzing your reviews today and see immediate results.
              </Typography>

              {/* Bullet Points */}
              <List sx={{ 
                mb: { xs: 3, md: 4 }, 
                width: 'fit-content', 
                mx: { xs: 'auto', md: 0 }, 
                px: { xs: 2, md: 0 } 
              }}>
                <ListItem disablePadding sx={{ mb: { xs: 0.5, md: 1 } }}>
                  <ListItemIcon sx={{ minWidth: { xs: 32, md: 36 } }}>
                    <CheckIcon color="primary" sx={{ fontSize: { xs: 20, md: 20 } }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="No account connections needed - analyze any business including competitors" 
                    primaryTypographyProps={{ fontSize: { xs: '1rem', md: '1.1rem' } }} 
                  />
                </ListItem>
                <ListItem disablePadding sx={{ mb: { xs: 0.5, md: 1 } }}>
                  <ListItemIcon sx={{ minWidth: { xs: 32, md: 36 } }}>
                    <CheckIcon color="primary" sx={{ fontSize: { xs: 20, md: 20 } }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Quantitative analysis shows exactly how customer experience affects revenue" 
                    primaryTypographyProps={{ fontSize: { xs: '1rem', md: '1.1rem' } }} 
                  />
                </ListItem>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: { xs: 32, md: 36 } }}>
                    <CheckIcon color="primary" sx={{ fontSize: { xs: 20, md: 20 } }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary="10x cheaper than existing solutions - pay monthly, cancel anytime" 
                    primaryTypographyProps={{ fontSize: { xs: '1rem', md: '1.1rem' } }} 
                  />
                </ListItem>
              </List>

              {/* CTA Buttons */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                gap: { xs: 1.5, sm: 2 }, 
                mb: { xs: 2, md: 2 } 
              }}>
                <CTAButton
                  variant="primary"
                  onClick={scrollToPricing}
                  sx={{
                    px: { xs: 5, md: 5 },
                    flex: { xs: 1, sm: 'auto' }
                  }}
                >
                  See What Customers Think
                </CTAButton>
                
                <CTAButton
                  variant="secondary"
                  onClick={handleDemoClick}
                  sx={{
                    px: { xs: 4, md: 4 },
                    flex: { xs: 1, sm: 'auto' }
                  }}
                >
                  Try Demo
                </CTAButton>
              </Box>

              {/* Social Proof */}
              <Typography
                variant="body2"
                sx={{
                  color: '#78909C',
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  px: { xs: 2, md: 0 },
                  mb: { xs: 3, md: 0 }
                }}
              >
                Trusted by 1,000s of business locations who started with Sashy and saw immediate ROI.
              </Typography>
            </Box>

            {/* Right Column: Visual */}
            <Box sx={{ 
              flexGrow: 1, 
              flexBasis: { xs: '100%', md: '40%' }, 
              minWidth: 0, 
              display: { xs: 'flex', md: 'flex' }, 
              flexDirection: 'column',
              alignItems: 'center' 
            }}>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <img
                  src="/hero_image.png"
                  alt="Abstract illustration showing fragmented data points feeding into a brain icon, leading to an upward trending bar chart representing insights and growth"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    borderRadius: '16px',
                    marginBottom: '24px'
                  }}
                />
              </Box>
              
              {/* Review Platforms - Below the hero image (Desktop only) */}
              <Box sx={{ 
                width: '100%',
                textAlign: 'center',
                mt: { xs: 2, md: 0 },
                display: { xs: 'none', md: 'block' }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 3,
                  mb: 2
                }}>
                  <img 
                    src="/images/sources/google-logo.png" 
                    alt="Google Reviews" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/tripadvisor-logo.png" 
                    alt="TripAdvisor" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/trustpilot-logo.png" 
                    alt="Trustpilot" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />

                  <img 
                    src="/images/sources/yelp-logo.png" 
                    alt="Yelp" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/booking-logo.png" 
                    alt="Booking.com" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/justeat-logo.png" 
                    alt="Just Eat" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/deliveroo-logo.png" 
                    alt="Deliveroo" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/ubereats-logo.png" 
                    alt="Uber Eats" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />
                  <img 
                    src="/images/sources/designmynight-logo.png" 
                    alt="Design My Night" 
                    style={{ height: '36px', width: 'auto', opacity: 0.8 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ 
                  color: '#78909C', 
                  fontSize: '0.8rem',
                  fontStyle: 'italic'
                }}>
                  + Custom integrations
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Bottom Spacer */}
      <Box sx={{ flex: 1 }} />
    </Box>
  );
};

export default HeroSection; 